import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Compass, TriangleAlert, Truck as TruckIcon, Search, Users } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";
import LoadingState from "./LoadingState";
import Pagination from "./Pagination";
import RouteMonitorModal from "../../components/RouteMonitorModal";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const STATUS_LABEL = { in_transit: "In transit", delivered: "Delivered", cancelled: "Cancelled", quoted: "Quoted" };

const PAGE_SIZE = 25;
const GROUPED_PAGE_SIZE = 200;

export default function RouteHistory() {
  const [start, setStart] = useState(daysAgoIso(30));
  const [end, setEnd] = useState(todayIso());
  const [rawSearch, setRawSearch] = useState("");
  const search = useDebounced(rawSearch, 300);
  const [groupByDispatcher, setGroupByDispatcher] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openLoad, setOpenLoad] = useState(null);
  const [page, setPage] = useState(1);

  const effectivePageSize = groupByDispatcher ? GROUPED_PAGE_SIZE : PAGE_SIZE;

  // A date-range/search/grouping change invalidates whatever page we were
  // on -- start back at page 1 rather than possibly requesting a page past
  // the new, narrower result set (off-route-history clamps an out-of-range
  // page to an empty "loads" list rather than erroring, so this is a UX
  // fix, not a correctness one).
  useEffect(() => {
    setPage(1);
  }, [start, end, search, groupByDispatcher]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = { start, end, page, page_size: effectivePageSize };
    if (search.trim()) params.search = search.trim();
    axios
      .get(`${apiBaseUrl}/scripted/fleet/off-route-history`, { ...authHeaders(), params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [start, end, page, effectivePageSize, search]);

  const loads = data?.loads || [];
  const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);

  const groups = useMemo(() => {
    if (!groupByDispatcher) return null;
    const byDispatcher = new Map();
    for (const l of loads) {
      const key = l.dispatcher_name || "Unassigned";
      if (!byDispatcher.has(key)) byDispatcher.set(key, []);
      byDispatcher.get(key).push(l);
    }
    return [...byDispatcher.entries()].sort(([a], [b]) => {
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });
  }, [groupByDispatcher, loads]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Route History</h1>
          <div style={styles.subtitle}>Loads whose truck deviated from its planned route or missed a planned fuel stop -- the after-the-fact audit view.</div>
        </div>
        <div style={styles.dateRange}>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={14} style={styles.searchIcon} />
          <input
            style={styles.searchInput}
            type="search"
            placeholder="Search by truck # or driver name..."
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          style={{ ...styles.groupBtn, ...(groupByDispatcher ? styles.groupBtnActive : {}) }}
          onClick={() => setGroupByDispatcher((v) => !v)}
        >
          <Users size={13} /> Group by dispatcher
        </button>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load route history: {error}</div>}

      {loading && <LoadingState label="Loading route history…" />}

      <div style={styles.statGrid}>
        <StatCard label="Loads flagged" value={loading ? "…" : data?.affected_loads_count ?? 0} icon={<Compass size={16} />} />
        <StatCard label="Trucks affected" value={loading ? "…" : data?.affected_trucks_count ?? 0} icon={<TruckIcon size={16} />} />
        <StatCard label="Total deviation events" value={loading ? "…" : data?.total_off_route_events ?? 0} icon={<TriangleAlert size={16} />} />
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>Flagged loads</div>
        {!loading && loads.length === 0 && !error && (
          <div style={styles.emptyNote}>
            {rawSearch.trim()
              ? `No flagged loads match "${rawSearch.trim()}" -- try a different truck # or driver name, or clear the search.`
              : "No route or fuel-stop exceptions in this range -- every truck stayed on plan."}
          </div>
        )}
        {loads.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Pickup</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Lane</th>
                  <th style={styles.th}>Truck</th>
                  <th style={styles.th}>Driver</th>
                  <th style={styles.th}>Dispatcher</th>
                  <th style={styles.th}>Events</th>
                  <th style={styles.th}>Max deviation</th>
                  <th style={styles.th}>Actual tolls</th>
                  <th style={styles.th}>Planned tolls</th>
                  <th style={styles.th}>Fuel stops</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {groupByDispatcher && groups
                  ? groups.map(([dispatcherName, groupLoads]) => (
                      <React.Fragment key={dispatcherName}>
                        <tr>
                          <td style={styles.groupRow} colSpan={12}>
                            <span style={styles.dispatcherGroupName}>{dispatcherName}</span>
                            <span style={styles.dispatcherGroupCount}>{groupLoads.length} load{groupLoads.length === 1 ? "" : "s"}</span>
                          </td>
                        </tr>
                        {groupLoads.map((l) => (
                          <LoadRow key={l.load_id} l={l} fmt={fmt} onClick={() => setOpenLoad(l)} />
                        ))}
                      </React.Fragment>
                    ))
                  : loads.map((l) => <LoadRow key={l.load_id} l={l} fmt={fmt} onClick={() => setOpenLoad(l)} />)}
              </tbody>
            </table>
          </div>
        )}
        {!groupByDispatcher && data && (
          <Pagination
            page={data.page || 1}
            totalPages={data.total_pages || 1}
            totalCount={data.total_count ?? loads.length}
            pageSize={data.page_size || PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
        <div style={styles.caption}>
          Actual tolls are real, GPS-detected passes priced against any route HERE quoted for this load -- the one
          dispatch picked, or one of the other candidates it didn't -- so a detour onto an alternate still gets a
          real price. Planned tolls are just the chosen route's original estimate, for comparison. Fuel stops shows
          how many of the load's planned fuel stops were actually hit (GPS-detected gallons rising near the planned
          station) -- "+N unplanned" means the truck fueled somewhere off the plan instead of or in addition to a
          planned stop. Click a row to play back planned vs. actual.
        </div>
      </div>

      {openLoad && (
        <RouteMonitorModal loadId={openLoad.load_id} unitNumber={openLoad.unit_number} onClose={() => setOpenLoad(null)} />
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statIcon}>{icon}</div>
      <div>
        <div style={styles.statValue}>{value}</div>
        <div style={styles.statLabel}>{label}</div>
      </div>
    </div>
  );
}

function LoadRow({ l, fmt, onClick }) {
  return (
    <tr style={styles.trClickable} onClick={onClick}>
      <td style={styles.td}>{l.pickup_datetime ? new Date(l.pickup_datetime).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "—"}</td>
      <td style={styles.td}>{l.customer_name || `${l.origin || "Origin"} → ${l.destination || "destination"}`}</td>
      <td style={styles.td}>{l.origin} → {l.destination}</td>
      <td style={styles.td}>{l.unit_number}</td>
      <td style={styles.td}>{l.driver_name || "Unassigned"}</td>
      <td style={styles.td}>{l.dispatcher_name}</td>
      <td style={{ ...styles.td, fontWeight: 700, color: "#e0745d" }}>{l.off_route_events}</td>
      <td style={styles.td}>{l.max_distance_off_route_miles}mi</td>
      <td style={styles.td}>{l.actual_toll_count > 0 ? fmt(l.actual_toll_total) : "—"}</td>
      <td style={styles.td}>{fmt(l.planned_toll_cost)}</td>
      <td style={styles.td}>
        {l.planned_fuel_stops > 0 ? (
          <span style={{ fontWeight: 700, color: l.missed_fuel_stops > 0 ? "#e0745d" : "var(--ink-2)" }}>
            {l.matched_fuel_stops}/{l.planned_fuel_stops}
          </span>
        ) : (
          "—"
        )}
        {l.unplanned_fuel_stops > 0 && (
          <span style={{ marginLeft: 6, fontSize: 11, color: "#e0745d" }}>
            +{l.unplanned_fuel_stops} unplanned
          </span>
        )}
      </td>
      <td style={styles.td}>
        <span style={styles.statusPill}>{STATUS_LABEL[l.status] || l.status}</span>
      </td>
    </tr>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  dateRange: { display: "flex", alignItems: "center", gap: 8 },
  dateInput: {
    fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 10px",
  },
  dateSep: { fontSize: 12, color: "var(--muted)" },

  toolbar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  searchWrap: { position: "relative", flex: "1 1 260px", minWidth: 180 },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" },
  searchInput: {
    width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px 8px 30px",
    borderRadius: 8, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)",
  },
  groupBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
    color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 12px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
  },
  groupBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  statGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 },
  statCard: {
    display: "flex", alignItems: "center", gap: 12, background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: "var(--radius-lg)", padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  statIcon: {
    width: 34, height: 34, borderRadius: 9, background: "rgba(224,116,93,0.12)", color: "#e0745d",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  statValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  statLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: 18, boxShadow: "var(--shadow-xs)",
  },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 12 },
  emptyNote: { fontSize: 12.5, color: "var(--muted)" },

  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", padding: "8px 6px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  td: { padding: "8px 6px", color: "var(--ink-2)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  trClickable: { cursor: "pointer" },
  groupRow: {
    padding: "10px 6px 6px", borderBottom: "1px solid var(--line)", display: "flex",
    alignItems: "baseline", gap: 8, background: "var(--bg-subtle, rgba(148,163,184,0.06))",
  },
  dispatcherGroupName: { fontSize: 13, fontWeight: 700, color: "var(--ink)" },
  dispatcherGroupCount: { fontSize: 11.5, color: "var(--muted)" },
  statusPill: {
    padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: "rgba(148,163,184,0.15)", color: "var(--ink-2)",
  },
  caption: { fontSize: 11, color: "var(--muted)", marginTop: 12 },
};
